import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import axios from 'axios';
import { Clock, Plus, ArrowLeft } from 'react-feather';
import { toast } from 'react-toastify';
import config from '../../../config';

import '../../ControlPanel/sadmin.css';
import '../Settings/settings.css';
import '../AdminSettings/admin-v3.css';
import v3SelectStyles from '../AdminSettings/v3Select';

const portalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: v3SelectStyles,
};

const FREQ_OPTIONS = [
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' },
];

const ScheduleReportView = () => {
    const { t } = useTranslation();
    const canManage = localStorage.getItem('manageScheduleReports') === '1';
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const login_id = localStorage.getItem('loginId');

    const [reports, setReports] = useState([]);
    const [scheduleReports, setScheduleReports] = useState([]);
    const [properties, setProperties] = useState([]);

    const [viewType, setViewType] = useState(''); // '' | 'Add' | 'Edit'
    const [scheduleReportId, setScheduleReportId] = useState('');
    const [scheduleReportName, setScheduleReportName] = useState('');
    const [reportId, setReportId] = useState('');
    const [recipients, setRecipients] = useState('');
    const [frequency, setFrequency] = useState('');
    const [alertTime, setAlertTime] = useState('09:00');
    const [disable, setDisable] = useState(false);
    const [selectedProperties, setSelectedProperties] = useState(''); // comma-joined property NAMES (backend format)
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!canManage) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canManage, t]);

    useEffect(() => {
        axios.post(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaire_id }, configheader)
            .then((res) => setProperties(res.data || [])).catch((e) => console.log(e));
        axios.get(`${config.hostname}/dynamicreports/getreports`, configheader)
            .then((res) => setReports((res.data || []).filter((r) => r.report_name !== 'Zero Sale Detection'))).catch((e) => console.log(e));
        fetchScheduleReports();
    }, []);

    const fetchScheduleReports = () => {
        axios.post(`${config.hostname}/schedulereports/getallschedulereport`, { concessionaire_id }, configheader)
            .then((res) => setScheduleReports(res.data || [])).catch((e) => console.error(e));
    };

    const reportOptions = useMemo(() => reports.map((r) => ({ value: r._id, label: r.report_name })), [reports]);
    const freqOptions = useMemo(() => FREQ_OPTIONS.map((o) => ({ ...o, label: t(o.label) })), [t]);
    const propertyOptions = useMemo(() => properties.map((p) => ({ value: p.property_name, label: p.property_name })), [properties]);
    const selectedPropertyOpts = useMemo(
        () => (selectedProperties ? selectedProperties.split(',').filter(Boolean).map((n) => ({ value: n, label: n })) : []),
        [selectedProperties],
    );

    const clearForm = () => {
        setViewType('');
        setScheduleReportId('');
        setScheduleReportName('');
        setReportId('');
        setRecipients('');
        setFrequency('');
        setAlertTime('09:00');
        setDisable(false);
        setSelectedProperties('');
    };

    const openEdit = async (id) => {
        try {
            const res = await axios.get(`${config.hostname}/schedulereports/getSchedulereportbyid/${id}`, configheader);
            setScheduleReportId(id);
            setScheduleReportName(res.data.schedule_report_name);
            setReportId(res.data.report_id);
            setRecipients(res.data.recipients);
            setFrequency(res.data.frequency);
            setAlertTime(res.data.alert_time);
            setDisable(res.data.disable);
            setSelectedProperties(res.data.property_access || '');
            setViewType('Edit');
        } catch (e) {
            console.error(e);
            toast.error(t('Error fetching report details'));
        }
    };

    const submit = (e) => {
        e.preventDefault();
        if (!reportId) return toast.warning(t('Select a report'));
        if (!frequency) return toast.warning(t('Select a frequency'));
        if (!selectedProperties) return toast.warning(t('Select at least one property'));
        const isEdit = viewType === 'Edit';
        const data = {
            concessionaire_id,
            login_id,
            report_id: reportId,
            schedule_report_name: scheduleReportName,
            recipients,
            frequency,
            alert_time: alertTime,
            created_by: login_id,
            property_access: selectedProperties,
            ...(isEdit ? { disable } : {}),
        };
        const url = isEdit
            ? `${config.hostname}/schedulereports/updateschedulereport/${scheduleReportId}`
            : `${config.hostname}/schedulereports/createschedulereport`;
        setSaving(true);
        axios.post(url, data, configheader)
            .then(() => {
                toast.success(isEdit ? t('Report updated successfully') : t('Report added successfully'));
                fetchScheduleReports();
                clearForm();
            })
            .catch((err) => { toast.error(t('Error saving report')); console.log(err); })
            .finally(() => setSaving(false));
    };

    if (!canManage) return null;

    const renderForm = (isEdit) => (
        <form className="v3-card" style={{ padding: 22 }} onSubmit={submit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div className="v3-field">
                    <label>{t('Report')}</label>
                    <Select {...portalProps} options={reportOptions}
                        value={reportOptions.find((o) => o.value === reportId) || null}
                        onChange={(opt) => setReportId(opt ? opt.value : '')}
                        placeholder={t('Select report…')} isSearchable />
                </div>
                <div className="v3-field">
                    <label>{t('Notification Name')}</label>
                    <input className="form-control" type="text" placeholder={t('Notification name')}
                        value={scheduleReportName} onChange={(e) => setScheduleReportName(e.target.value)} required />
                </div>
                <div className="v3-field" style={{ gridColumn: '1 / -1' }}>
                    <label>{t('Recipients')}</label>
                    <input className="form-control" type="text" placeholder={t('Comma-separated email addresses')}
                        value={recipients} onChange={(e) => setRecipients(e.target.value)} required />
                </div>
                <div className="v3-field">
                    <label>{t('Frequency')}</label>
                    <Select {...portalProps} options={freqOptions}
                        value={freqOptions.find((o) => o.value === frequency) || null}
                        onChange={(opt) => setFrequency(opt ? opt.value : '')}
                        placeholder={t('Select frequency…')} isSearchable={false} />
                </div>
                <div className="v3-field">
                    <label>{t('Alert Time')}</label>
                    <input className="form-control" type="time" value={alertTime}
                        onChange={(e) => setAlertTime(e.target.value)} required />
                </div>
                <div className="v3-field" style={{ gridColumn: '1 / -1' }}>
                    <label>{t('Properties')}</label>
                    <Select {...portalProps} isMulti options={propertyOptions}
                        value={selectedPropertyOpts}
                        onChange={(opts) => setSelectedProperties((opts || []).map((o) => o.value).join(','))}
                        placeholder={t('Search & select properties…')} closeMenuOnSelect={false} />
                </div>
                {isEdit && (
                    <label className="v3-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <input type="checkbox" checked={!!disable} onChange={(e) => setDisable(e.target.checked)} />
                        <span>{t('Disable this schedule')}</span>
                    </label>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                    {saving ? t('Saving…') : isEdit ? t('Update report') : t('Save report')}
                </button>
            </div>
        </form>
    );

    return (
        <Fragment>
            <div className="v3-sadmin v3-admin-page">
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">
                                {viewType === 'Add' ? t('New Schedule') : viewType === 'Edit' ? t('Edit Schedule') : t('Schedule Reports')}
                            </span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">{t('Email scheduled reports to recipients on a recurring cadence.')}</p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        {viewType === '' ? (
                            <button type="button" className="v3-btn v3-btn-primary" onClick={() => setViewType('Add')}>
                                <Plus size={15} /> {t('Add New')}
                            </button>
                        ) : (
                            <button type="button" className="v3-btn v3-btn-ghost" onClick={clearForm}>
                                <ArrowLeft size={15} /> {t('Back')}
                            </button>
                        )}
                        <span className="v3-sadmin-kpi-icon accent" aria-hidden><Clock size={18} /></span>
                    </div>
                </div>

                {viewType === '' && (
                    <div className="v3-card v3-sadmin-list-card">
                        <div className="v3-sadmin-list-header">
                            <div className="v3-sadmin-list-title">
                                <h2 className="v3-title">{t('Schedules')}</h2>
                                <span className="v3-sadmin-list-count">{scheduleReports.length}</span>
                            </div>
                        </div>
                        {scheduleReports.length === 0 ? (
                            <div className="v3-sadmin-empty"><h3>{t('No schedules yet')}</h3><p>{t('Add a scheduled report to get started.')}</p></div>
                        ) : (
                            <div className="v3-sadmin-table-wrap">
                                <table className="table" style={{ margin: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>#</th><th>{t('Notification Name')}</th><th>{t('Frequency')}</th><th>{t('Time')}</th><th>{t('Status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scheduleReports.map((item, index) => (
                                            <tr key={item._id} onClick={() => openEdit(item._id)} style={{ cursor: 'pointer' }}>
                                                <td>{index + 1}</td>
                                                <td>{item.schedule_report_name}</td>
                                                <td>{t(item.frequency)}</td>
                                                <td>{item.alert_time}</td>
                                                <td>
                                                    <span className={`v3-badge ${item.disable ? 'muted' : 'green'}`}>
                                                        {item.disable ? t('Disabled') : t('Active')}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {viewType === 'Add' && renderForm(false)}
                {viewType === 'Edit' && renderForm(true)}
            </div>
        </Fragment>
    );
};

export default ScheduleReportView;
